import type {
  PlayerTournamentProgressSchema,
  PrizeSchema,
  PublicCampaignSchema,
} from "@/types";
import { campaignKey, matchesKey } from "@/campaigns/campaign-key";
import type { CampaignKey } from "@/campaigns/campaign-key";
import { toGameNames } from "@/campaigns/eligible-games";
import { foldTasks } from "../missions/assemble";
import type { Chase, ChaseRung } from "./types";

/**
 * Mark the rungs this player has won from the progress push's `prizes` (one row per payout).
 *
 * By `attempts` when the row carries it (a backend with #4041), which names the exact rung;
 * by title otherwise, consuming each matching rung once so two rungs with the same title
 * ("25 Free Spins" twice) degrade to "one of them" rather than both lighting up.
 */
function markWon(
  rungs: ChaseRung[],
  winnings: PlayerTournamentProgressSchema["prizes"] | undefined
): ChaseRung[] {
  if (!winnings?.length) return rungs;
  const consumed = new Set<string>();
  for (const winning of winnings) {
    const match =
      winning.attempts != null
        ? rungs.find(
            (rung) => rung.budget === winning.attempts && !consumed.has(rung.id)
          )
        : rungs.find(
            (rung) =>
              rung.prize.title === winning.title && !consumed.has(rung.id)
          );
    if (match) consumed.add(match.id);
  }
  return rungs.map((rung) =>
    consumed.has(rung.id) ? { ...rung, won: true } : rung
  );
}

/** The ladder from the structural prizes: only budgeted rows, cheapest budget first. */
function toRungs(key: CampaignKey, prizes: PrizeSchema[]): ChaseRung[] {
  return prizes
    .filter((prize) => prize.attempts != null)
    .sort((a, b) => (a.attempts ?? 0) - (b.attempts ?? 0))
    .map((prize, index) => ({
      id: `${key}:${prize.attempts}:${index}`,
      budget: prize.attempts as number,
      prize,
      won: false,
    }));
}

/**
 * Join the two streams into the rendered `Chase[]`. Pure — the store calls it (via `derive.ts`)
 * after either updates.
 *
 * Claims `type "TO"` + `subtype "IM"`; `assembleTournaments` skips `"IM"` for the same reason
 * the two mission modules split on subtype: `resolveCurrentCampaign` takes the FIRST module
 * whose `resolve` matches, so a campaign both claimed would render as a leaderboard tournament.
 *
 * A chase is included as soon as its `campaigns` tile arrives, with `progress: null` until the
 * player's `tournament_progress` row lands — so the go-live / opt-in tile is renderable without
 * a confident-looking "0 spins".
 */
export function assembleChases(
  campaignsById: Record<CampaignKey, PublicCampaignSchema>,
  progressItems: PlayerTournamentProgressSchema[]
): Chase[] {
  const result: Chase[] = [];

  for (const campaign of Object.values(campaignsById)) {
    if (campaign.type !== "TO" && campaign.type !== "RA") continue;
    if (campaign.subtype !== "IM") continue;

    const key = campaignKey(campaign);
    const progress = progressItems.find((row) => matchesKey(row, key));
    const { games, count } = toGameNames(
      campaign.eligible_games,
      campaign.eligible_games_count
    );
    const mb = campaign.minimum_bet;

    result.push({
      campaign,
      minimum_bet: mb
        ? { value: mb.value, currency: mb.currency, display: mb.display }
        : undefined,
      rungs: markWon(toRungs(key, campaign.prizes ?? []), progress?.prizes),
      // The structural list and, when the push carries it, this player's state per target —
      // the same fold the mission ladders use, joined on `order`.
      targets: foldTasks(
        key,
        campaign.tasks ?? [],
        progress?.tasks ?? undefined
      ),
      spinsBeforeRestart: campaign.spins_before_restart ?? null,
      eligible_games: games,
      eligible_games_count: count,
      progress: progress
        ? {
            current: progress.current,
            total: progress.total,
            can_reset: progress.can_reset,
            hasTargetState: progress.tasks != null,
          }
        : null,
    });
  }

  return result;
}
